import { HttpClient, HttpHeaders, type HttpErrorResponse, type HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { throwError, type Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ConfigService, SentryLogger } from '@fcom/core/services';

import {
  GiftCardVerificationResponse,
  type GiftCardConversionPayload,
  type GiftCardVerificationPayload,
} from '../interfaces';

@Injectable()
export class GiftCardService {
  giftCardConversionUrl: string;
  giftCardVerificationUrl: string;

  constructor(
    private configService: ConfigService,
    private http: HttpClient,
    private router: Router,
    private sentryLogger: SentryLogger
  ) {
    this.giftCardConversionUrl = this.configService.cfg.giftCardConversionUrl;
    this.giftCardVerificationUrl = this.configService.cfg.giftCardVerificationUrl;
  }

  /**
   * Adds headers to outgoing API requests. This includes the
   * `content-type`, `X-Api-Key` and any extra headers that
   * have been provided.
   *
   * @default contentType 'application/json'
   */
  private createHeaders(
    extraHeaders: Record<string, unknown> = {},
    contentType = 'application/json; charset=utf-8'
  ): HttpHeaders {
    return new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': contentType,
      'X-Api-Key': this.configService.cfg.giftCardApiKey,
      ...extraHeaders,
    });
  }

  /**
   * Verifies gift card based on the ID and PIN given.
   * @see https://checkout-reference-preprod.preprod.payments.app.cloud.finnair.com/giftCard
   * @returns The gift card information if accepted.
   */
  getGiftCardInformation({
    giftCardId,
    pin,
  }: GiftCardVerificationPayload): Observable<GiftCardVerificationResponse | HttpErrorResponse> {
    return this.http
      .get<GiftCardVerificationResponse>(`${this.giftCardVerificationUrl}/${giftCardId}`, {
        headers: this.createHeaders({ pin }),
      })
      .pipe(
        catchError((response: HttpErrorResponse): Observable<HttpErrorResponse> => {
          this.sentryLogger.warn('Unable to get gift card information', { response });
          return throwError((): unknown => response);
        })
      );
  }

  /**
   * This will return the parent page by
   * removing the last segment of current url.
   *
   * @example '/fi-en/customer-care-and-contact-information/contact-and-request-forms/check-your-gift-card' ->
   * '/fi-en/customer-care-and-contact-information/contact-and-request-forms'
   */
  getReturnUrl(): string {
    return this.router.url.split('/').slice(0, -1).join('/');
  }

  /**
   * Makes the request to convert the gift card to cash.
   *
   * Includes the customer's gift card ID, PIN, bank details and email address.
   */
  postGiftCardConversion({
    body,
    giftCardId,
    pin,
  }: GiftCardConversionPayload): Observable<HttpResponse<Response> | HttpErrorResponse> {
    return this.http
      .post<HttpResponse<Response>>(`${this.giftCardConversionUrl}/${giftCardId}`, body, {
        headers: this.createHeaders({ pin }),
      })
      .pipe(
        catchError((response: HttpErrorResponse): Observable<HttpErrorResponse> => {
          this.sentryLogger.warn('Unable to convert gift card to cash', { response });
          return throwError((): unknown => response);
        })
      );
  }
}
